import { getSingular } from "mypluralize"
import axios from "axios"
import { InterpreterFrom, StateFrom } from "xstate"

import { SimulatorModel, AuthItem, EndpointFilter, EndpointData, SimulatorModelContext } from "./SimulatorModel"
import { VisibleQueryEndpoint, VisibleMutationEndpoint } from "./SimulatorModel"
import { GetEntity, GetEntityAllFields } from "../helpers/functions"
import { LocalStorageSessions } from "../pages/Simulator"

/**
 * Helper function to check if endpoint is ready to run
 *
 * @param context context of the Simulator machine
 * @param selectedEndpointData Endpoint data object from iside of the context
 * @returns array of strings with error messages
 */
const checkEndpointRunablity = (context: SimulatorModelContext, selectedEndpointName: string, selectedEndpointData: EndpointData) => {
    const preventRunErrors: string[] = []

    if ((selectedEndpointName.startsWith("get") || selectedEndpointName.startsWith("list")) && selectedEndpointData.selectorFields.length === 0)
        preventRunErrors.push("Select at least one field to return in the response")

    if (selectedEndpointData.filters) {
        for (const filter of selectedEndpointData.filters) {
            if (!filter.field) preventRunErrors.push("Fulfil empty filter field")
            if (!filter.operator) preventRunErrors.push("Fulfil empty filter operator")
            if (!filter.value) preventRunErrors.push("Fulfil empty filter value")
        }
    }

    if (
        (selectedEndpointName.startsWith("get") || selectedEndpointName.startsWith("update") || selectedEndpointName.startsWith("delete")) &&
        !selectedEndpointData.objectId
    )
        preventRunErrors.push("Provide ID of the object")

    if (selectedEndpointName.startsWith("create")) {
        const entityName = selectedEndpointName.slice(6)
        const entityFields = GetEntityAllFields(context.projectConfig, entityName, true, true)
        if (entityFields) {
            for (const field of entityFields) {
                if (field.mandatory && !selectedEndpointData.inputs[field.name] && !selectedEndpointData.inputs[field.name + "_FILE"]) {
                    preventRunErrors.push(`Fulfil empty required field ${field.name}`)
                }
            }
        }
    }

    return preventRunErrors
}

export const simulatorMachine = SimulatorModel.createMachine(
    {
        /** @xstate-layout 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 */
        id: "simulator",

        // Local context for entire machine
        context: SimulatorModel.initialContext,

        // Initial state
        initial: "none",

        // globally defined actions
        on: {
            UPDATE_NOTIFICATIONS_API: {
                actions: SimulatorModel.assign({ notificationsApi: (ctx, e) => e.notificationsApi }),
            },
            SELECT_SIMULATOR_ENV: {
                actions: [
                    SimulatorModel.assign({
                        gqlSimulator: (context, event) => ({
                            ...context.gqlSimulator,
                            env: event.env,
                        }),
                        apiAuth: (ctx, e) => {
                            const sessionsDictString = localStorage.getItem("simulator-sessions") || "{}"
                            const sessionsDict = sessionsDictString ? JSON.parse(sessionsDictString) : ({} as LocalStorageSessions)
                            return {
                                ...ctx.apiAuth,
                                authSessionsList: sessionsDict[ctx.projectId]?.[e.env] ?? [],
                                currentAuthSession: undefined,
                            }
                        },
                    }),
                    "trackAction",
                ],
            },
        },

        // States
        states: {
            none: {
                on: {
                    FETCH: {
                        target: "globalLoading",
                        actions: SimulatorModel.assign({
                            apiAuth: (ctx, e) => {
                                const sessionsDictString = localStorage.getItem("simulator-sessions") || "{}"
                                const sessionsDict = sessionsDictString ? JSON.parse(sessionsDictString) : ({} as LocalStorageSessions)

                                return {
                                    ...ctx.apiAuth,
                                    authSessionsList: sessionsDict[e.projectId]?.[ctx.gqlSimulator.env] ?? [],
                                }
                            },
                            projectId: (_, e) => e.projectId,
                            sessionId: (_, e) => e.sessionId,
                        }),
                    },
                },
            },
            error: {
                on: {
                    TRY_AGAIN: {
                        target: "globalLoading",
                    },
                },
            },
            globalLoading: {
                type: "atomic",
                invoke: {
                    id: "simulatorMachine-getProject",
                    src: "getProject",
                },
                on: {
                    FETCHED: {
                        target: "default.tabs.simulatorWrapper",
                        actions: [
                            SimulatorModel.assign((context, event) => {
                                const endpointsData: { [key: string]: any } = {}
                                for (const ge of event.generatedEndpoints.queries.get) {
                                    endpointsData[ge.name] = {
                                        status: "",
                                        filters: [],
                                        selectorFields: ["id"],
                                        inputs: {},
                                    }
                                }

                                return {
                                    projectConfig: event.projectConfig,
                                    generatedEndpoints: event.generatedEndpoints,
                                    clientTypes: event.clientTypes,
                                    queries: event.queries,
                                    mutations: event.mutations,
                                    gqlSimulator: {
                                        ...context.gqlSimulator,
                                        endpointsData,
                                    },
                                }
                            }),
                            "prepareEndpoints",
                        ],
                    },
                    ERROR: {
                        target: "error",
                    },
                },
            },
            default: {
                type: "parallel",
                states: {
                    authTokenWorker: {
                        initial: "idle",
                        on: {
                            SELECT_SESSION: {
                                target: ".refreshingToken",
                                actions: [
                                    SimulatorModel.assign({
                                        apiAuth: (ctx, e) => ({
                                            ...ctx.apiAuth,
                                            currentAuthSession: ctx.apiAuth.authSessionsList[e.sessionIndex],
                                        }),
                                        gqlSimulator: (context) => ({
                                            ...context.gqlSimulator,
                                            invitationEndpoint: {},
                                            visibleInvitationUserGroups: [],
                                            acceptInvitationEndpoint: {},
                                        }),
                                    }),
                                    "closeEndpoint",
                                    "trackAction",
                                ],
                            },
                        },
                        states: {
                            idle: {},
                            refreshingToken: {
                                type: "atomic",
                                invoke: {
                                    id: "simulatorMachine-refreshAuthToken",
                                    src: "refreshAuthToken",
                                },
                                on: {
                                    AUTH_TOKEN_REFRESHED: {
                                        actions: [
                                            SimulatorModel.assign({
                                                apiAuth: (ctx, e) => ({
                                                    ...ctx.apiAuth,
                                                    currentAuthSession: e.currentAuthSession,
                                                    authSessionsList: e.authSessionsList,
                                                }),
                                            }),
                                            "prepareEndpoints",
                                            "checkAllRunability",
                                            "trackAction",
                                        ],
                                        target: "idle",
                                    },
                                },
                            },
                        },
                    },
                    tabs: {
                        initial: "simulatorWrapper",
                        states: {
                            simulatorWrapper: {
                                id: "simulatorWrapper",
                                type: "parallel",
                                on: {
                                    DOCS: { target: "docs", actions: "trackAction" },
                                    API_USERS: { target: "apiUsers", actions: "trackAction" },
                                    LOGIN_SESSIONS: { target: "#login-session", actions: "trackAction" },
                                },
                                states: {
                                    gqlSimulator: {
                                        initial: "observeEndpointsList",
                                        on: {
                                            OPEN_FILTER: {
                                                target: ".queryFilter",
                                                actions: [
                                                    "selectEndpoint",
                                                    SimulatorModel.assign({
                                                        gqlSimulator: (context) => {
                                                            const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                context.gqlSimulator.selectedEndpoint
                                                            ] ?? {
                                                                status: "",
                                                                filters: [],
                                                                selectorFields: ["id"],
                                                                inputs: {},
                                                            }

                                                            // Commented out because we don't need to add empty filter rule by default to avoid incorrect query
                                                            //
                                                            // if (selectedEndpointData.filters.length === 0)
                                                            //     selectedEndpointData.filters = [
                                                            //         {
                                                            //             field: "",
                                                            //             operator: "EQUAL",
                                                            //             // value: "",
                                                            //         },
                                                            //     ]

                                                            return {
                                                                ...context.gqlSimulator,
                                                                endpointsData: {
                                                                    ...context.gqlSimulator.endpointsData,
                                                                    [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                },
                                                            }
                                                        },
                                                    }),
                                                    "checkRunability",
                                                    "trackAction",
                                                ],
                                            },
                                            OPEN_SELECTOR_FIELDS: {
                                                target: ".querySelectorFields",
                                                actions: ["selectEndpoint", "checkRunability", "trackAction"],
                                            },
                                            OPEN_MUTATION_FORM: {
                                                target: ".mutationForm",
                                                actions: ["selectEndpoint", "checkRunability", "trackAction"],
                                            },
                                            OPEN_CODE_SAMPLE: {
                                                target: ".codeSample",
                                                actions: ["selectEndpoint", "checkRunability", "constructEndpointPayload", "trackAction"],
                                            },
                                            COLLAPSE_ALL: {
                                                target: ".observeEndpointsList",
                                            },
                                            SELECT_ENDPOINT: {
                                                target: ".observeEndpointsList",
                                                actions: ["selectEndpoint", "checkRunability", "trackAction"],
                                            },
                                            CLOSE_ENDPOINT: {
                                                target: ".observeEndpointsList",
                                                actions: ["checkRunability", "closeEndpoint", "trackAction"],
                                            },
                                            SET_OBJECTID: {
                                                actions: [
                                                    SimulatorModel.assign({
                                                        gqlSimulator: (context, event) => {
                                                            const selectedEndpointData = context.gqlSimulator.endpointsData[event.endpointName] ?? {
                                                                status: "",
                                                                filters: [],
                                                                selectorFields: ["id"],
                                                                inputs: {},
                                                            }
                                                            selectedEndpointData.objectId = event.objectId
                                                            return {
                                                                ...context.gqlSimulator,
                                                                endpointsData: {
                                                                    ...context.gqlSimulator.endpointsData,
                                                                    [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                },
                                                            }
                                                        },
                                                    }),
                                                    "checkRunability",
                                                ],
                                            },
                                            SET_ACCEPT_INVITATION_EMAIL: {
                                                actions: [
                                                    SimulatorModel.assign({
                                                        gqlSimulator: (context, event) => {
                                                            return {
                                                                ...context.gqlSimulator,
                                                                acceptInvitationEndpoint: {
                                                                    ...context.gqlSimulator.acceptInvitationEndpoint,
                                                                    email: event.email,
                                                                },
                                                            }
                                                        },
                                                    }),
                                                ],
                                            },
                                            SET_ACCEPT_INVITATION_CODE: {
                                                actions: [
                                                    SimulatorModel.assign({
                                                        gqlSimulator: (context, event) => {
                                                            return {
                                                                ...context.gqlSimulator,
                                                                acceptInvitationEndpoint: {
                                                                    ...context.gqlSimulator.acceptInvitationEndpoint,
                                                                    invitationCode: event.invitationCode,
                                                                },
                                                            }
                                                        },
                                                    }),
                                                ],
                                            },
                                            SET_ACCEPT_INVITATION_PASSWORD: {
                                                actions: [
                                                    SimulatorModel.assign({
                                                        gqlSimulator: (context, event) => {
                                                            return {
                                                                ...context.gqlSimulator,
                                                                acceptInvitationEndpoint: {
                                                                    ...context.gqlSimulator.acceptInvitationEndpoint,
                                                                    password: event.password,
                                                                },
                                                            }
                                                        },
                                                    }),
                                                ],
                                            },
                                        },
                                        states: {
                                            observeEndpointsList: {
                                                on: {},
                                            },
                                            queryFilter: {
                                                on: {
                                                    // TODO: need to refactor based on the and/or filter logic (maybe take from designer permissions if exists)
                                                    CLOSE: {
                                                        target: "observeEndpointsList",
                                                        actions: ["closeEndpoint", "trackAction"],
                                                    },
                                                    ADD_RULE: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    const newRule = {
                                                                        field: "",
                                                                        operator: "EQUAL",
                                                                        // value: "",
                                                                    } as EndpointFilter
                                                                    if (!selectedEndpointData.filters || selectedEndpointData.filters.length === 0) {
                                                                        selectedEndpointData.filters = [newRule]
                                                                    } else {
                                                                        selectedEndpointData.filters.push(newRule)
                                                                    }

                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                            "trackAction",
                                                        ],
                                                    },
                                                    REMOVE_RULE: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    selectedEndpointData.filters.splice(event.ruleIndex, 1)
                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                            "trackAction",
                                                        ],
                                                    },
                                                    CHANGE_RULE_FIELD: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    selectedEndpointData.filters[event.ruleIndex].field = event.field
                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                            "trackAction",
                                                        ],
                                                    },
                                                    CHANGE_OPERATOR: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    selectedEndpointData.filters[event.ruleIndex].operator =
                                                                        event.operator as EndpointFilter["operator"]
                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                            "trackAction",
                                                        ],
                                                    },
                                                    CHANGE_VALUE: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    selectedEndpointData.filters[event.ruleIndex].value = event.value
                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                        ],
                                                    },
                                                },
                                            },
                                            querySelectorFields: {
                                                on: {
                                                    CLOSE: { target: "observeEndpointsList", actions: ["closeEndpoint", "trackAction"] },
                                                    SELECT_FIELD: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    if (!selectedEndpointData.selectorFields.includes(event.field)) {
                                                                        selectedEndpointData.selectorFields.push(event.field)
                                                                    }
                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                            "trackAction",
                                                        ],
                                                    },
                                                    UNSELECT_FIELD: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    const unselectFields = [event.field].concat(
                                                                        selectedEndpointData.selectorFields.filter((f) =>
                                                                            f.startsWith(event.field + ".")
                                                                        )
                                                                    )
                                                                    if (unselectFields) {
                                                                        for (const unselectField of unselectFields) {
                                                                            const index = selectedEndpointData.selectorFields.findIndex(
                                                                                (field) => field === unselectField
                                                                            )
                                                                            if (index !== undefined && index !== -1) {
                                                                                selectedEndpointData.selectorFields.splice(index, 1)
                                                                            }
                                                                        }
                                                                    }
                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                            "trackAction",
                                                        ],
                                                    },
                                                },
                                            },
                                            mutationForm: {
                                                on: {
                                                    CLOSE: { target: "observeEndpointsList", actions: ["closeEndpoint", "trackAction"] },
                                                    SET_MUTATION_FORM_FIELDS_VALUES: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const selectedEndpointData = context.gqlSimulator.endpointsData[
                                                                        context.gqlSimulator.selectedEndpoint
                                                                    ] ?? {
                                                                        status: "",
                                                                        filters: [],
                                                                        selectorFields: ["id"],
                                                                        inputs: {},
                                                                    }
                                                                    selectedEndpointData.inputs = event.values
                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData: {
                                                                            ...context.gqlSimulator.endpointsData,
                                                                            [context.gqlSimulator.selectedEndpoint]: selectedEndpointData,
                                                                        },
                                                                    }
                                                                },
                                                            }),
                                                            "checkRunability",
                                                            "trackAction",
                                                        ],
                                                    },
                                                    // not sure if we need to implement this here
                                                    // SELECT_SUB_OBJECT: {},
                                                    // UNSELECT_SUB_OBJECT: {},
                                                },
                                            },
                                            codeSample: {
                                                on: {
                                                    CLOSE: { target: "observeEndpointsList", actions: ["closeEndpoint", "trackAction"] },
                                                },
                                            },
                                        },
                                    },
                                    response: {
                                        initial: "hidden",
                                        on: {
                                            RUN: {
                                                target: ".running",
                                                actions: [
                                                    "selectEndpoint",
                                                    "checkRunability",
                                                    SimulatorModel.assign({
                                                        gqlSimulator: (context) => ({
                                                            ...context.gqlSimulator,
                                                            endpointsData: {
                                                                ...context.gqlSimulator.endpointsData,
                                                                [context.gqlSimulator.selectedEndpoint]: {
                                                                    ...context.gqlSimulator.endpointsData[context.gqlSimulator.selectedEndpoint],
                                                                    status: "loading",
                                                                    response: {
                                                                        startTime: new Date(),
                                                                    },
                                                                },
                                                            },
                                                        }),
                                                    }),
                                                    "constructEndpointPayload",
                                                    "trackAction",
                                                ],
                                            },
                                            HIDE: {
                                                target: ".hidden",
                                                actions: [
                                                    SimulatorModel.assign({
                                                        gqlSimulator: (context) => ({
                                                            ...context.gqlSimulator,
                                                            selectedEndpoint: "",
                                                        }),
                                                    }),
                                                    "trackAction",
                                                ],
                                            },
                                            SHOW_RESPONSE: {
                                                target: ".tree", // FIXME: could be that tree does not exist for selected endpoint type
                                                actions: ["selectEndpoint", "trackAction"],
                                            },
                                        },
                                        states: {
                                            hidden: {
                                                on: {},
                                            },
                                            running: {
                                                type: "atomic",
                                                invoke: {
                                                    id: "SimulatorMacine-runEndpoint",
                                                    src: "runEndpoint",
                                                },
                                                on: {
                                                    COMPLETED: {
                                                        target: "raw",
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                gqlSimulator: (context, event) => {
                                                                    const endpointsData = context.gqlSimulator.endpointsData

                                                                    if (!event.endpointName) return context.gqlSimulator

                                                                    const selectedEndpointData = endpointsData[event.endpointName]

                                                                    endpointsData[event.endpointName] = {
                                                                        ...selectedEndpointData,
                                                                        status: event.raw.errors?.length > 0 ? "error" : "success",
                                                                        response: {
                                                                            ...selectedEndpointData.response,
                                                                            startTime: selectedEndpointData.response!.startTime,
                                                                            endTime: event.endTime,
                                                                            onBehalf: context.apiAuth.currentAuthSession?.username,
                                                                            errors: event.raw.errors as { message: string }[],
                                                                            data: event.raw.data as {
                                                                                [key: string]: any
                                                                            },
                                                                        },
                                                                    }

                                                                    return {
                                                                        ...context.gqlSimulator,
                                                                        endpointsData,
                                                                    }
                                                                },
                                                            }),
                                                            "trackAction",
                                                        ],
                                                    },
                                                },
                                            },
                                            raw: {
                                                on: {
                                                    TABLE: { target: "table", actions: "trackAction" },
                                                    TREE: { target: "tree", actions: "trackAction" },
                                                },
                                            },
                                            table: {
                                                on: {
                                                    RAW: { target: "raw", actions: "trackAction" },
                                                    TREE: { target: "tree", actions: "trackAction" },
                                                },
                                            },
                                            tree: {
                                                on: {
                                                    RAW: { target: "raw", actions: "trackAction" },
                                                    TABLE: { target: "table", actions: "trackAction" },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                            docs: {
                                on: {
                                    SIMULATOR: { target: "simulatorWrapper", actions: "trackAction" },
                                    API_USERS: { target: "apiUsers", actions: "trackAction" },
                                    TYPESCRIPT_TYPES: { target: ".typescriptTypes", actions: "trackAction" },
                                    GRAPHQL_QUERIES: { target: ".graphqlQueries", actions: "trackAction" },
                                    GRAPHQL_MUTATIONS: { target: ".graphqlMutations", actions: "trackAction" },
                                },
                                initial: "graphqlQueries",
                                states: {
                                    graphqlQueries: {},
                                    graphqlMutations: {},
                                    typescriptTypes: {},
                                },
                            },
                            apiUsers: {
                                on: {
                                    SIMULATOR: { target: "simulatorWrapper", actions: "trackAction" },
                                    DOCS: { target: "docs", actions: "trackAction" },
                                },
                                initial: "selfSignup",
                                states: {
                                    loginSession: {
                                        id: "login-session",
                                        on: {
                                            TO_SELF_SINGUP: { target: "selfSignup", actions: "trackAction" },
                                            TO_EMAIL_INVITATION: { target: "emailInvitation", actions: "trackAction" },
                                            GO_SIMULATOR: { target: "#simulatorWrapper", actions: "trackAction" },
                                        },
                                        initial: "checkingState",
                                        states: {
                                            checkingState: {
                                                always: [
                                                    {
                                                        target: "login",
                                                        cond: (context: SimulatorModelContext) => context.apiAuth.authSessionsList.length === 0,
                                                    },
                                                    {
                                                        target: "sessions",
                                                        cond: (context: SimulatorModelContext) => context.apiAuth.authSessionsList.length > 0,
                                                    },
                                                ],
                                            },
                                            login: {
                                                on: {
                                                    GO_SESSIONS: { target: "sessions", actions: "trackAction" },
                                                },
                                                initial: "loginForm",
                                                states: {
                                                    loginForm: {
                                                        on: {
                                                            SELECT_USER: {
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (context, event) => ({
                                                                            ...context.apiAuth,
                                                                            authType: "USERGROUP",
                                                                            userGroup: event.userGroup,
                                                                        }),
                                                                    }),
                                                                    "trackAction",
                                                                ],
                                                            },
                                                            SET_USERNAME: {
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (context, event) => ({
                                                                            ...context.apiAuth,
                                                                            loginForm: {
                                                                                ...context.apiAuth.loginForm,
                                                                                username: event.username.toLowerCase(),
                                                                            },
                                                                        }),
                                                                    }),
                                                                ],
                                                            },
                                                            SET_PASSWORD: {
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (context, event) => ({
                                                                            ...context.apiAuth,
                                                                            loginForm: {
                                                                                ...context.apiAuth.loginForm,
                                                                                password: event.password,
                                                                            },
                                                                        }),
                                                                    }),
                                                                ],
                                                            },
                                                            LOGIN_CALL: {
                                                                target: "logingIn",
                                                                actions: "trackAction",
                                                            },
                                                        },
                                                    },
                                                    logingIn: {
                                                        type: "atomic",
                                                        invoke: {
                                                            id: "simulatorMachine-logIn",
                                                            src: "logIn",
                                                        },
                                                        on: {
                                                            LOGED_IN: {
                                                                target: "#login-sessions",
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (context, event) => {
                                                                            const newSession = {
                                                                                authType: "USERGROUP",
                                                                                username: context.apiAuth.loginForm.username,
                                                                                userGroup: context.apiAuth.userGroup,
                                                                                authToken: event.authToken,
                                                                                refreshToken: event.refreshToken,
                                                                            } as AuthItem

                                                                            return {
                                                                                ...context.apiAuth,
                                                                                currentAuthSession: newSession,
                                                                                authSessionsList: [...context.apiAuth.authSessionsList, newSession],
                                                                                userGroup: "",
                                                                            }
                                                                        },
                                                                    }),
                                                                    "prepareEndpoints",
                                                                    "closeEndpoint",
                                                                    "checkAllRunability",
                                                                    "trackAction",
                                                                    "trackAction",
                                                                ],
                                                            },
                                                            ERROR: {
                                                                target: "loginForm",
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                            sessions: {
                                                id: "login-sessions",
                                                on: {
                                                    GO_LOGIN: { target: "login", actions: "trackAction" },
                                                    SESSION_LOGOUT: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                apiAuth: (context) => ({
                                                                    ...context.apiAuth,
                                                                    currentAuthSession: undefined,
                                                                }),
                                                                gqlSimulator: (context) => ({
                                                                    ...context.gqlSimulator,
                                                                    invitationEndpoint: {},
                                                                    visibleInvitationUserGroups: [],
                                                                    acceptInvitationEndpoint: {},
                                                                }),
                                                            }),
                                                            "prepareEndpoints",
                                                            "closeEndpoint",
                                                            "trackAction",
                                                        ],
                                                    },
                                                    SESSION_DELETE: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                apiAuth: (context, event) => {
                                                                    const newSessionsList = context.apiAuth.authSessionsList.filter(
                                                                        (s, i) => i !== event.sessionIndex
                                                                    )
                                                                    return {
                                                                        ...context.apiAuth,
                                                                        authSessionsList: newSessionsList,
                                                                    }
                                                                },
                                                            }),
                                                            "trackAction",
                                                        ],
                                                    },
                                                },
                                            },
                                        },
                                    },
                                    selfSignup: {
                                        on: {
                                            TO_LOGIN_SESSION: { target: "loginSession", actions: "trackAction" },
                                            TO_EMAIL_INVITATION: { target: "emailInvitation", actions: "trackAction" },
                                        },
                                        initial: "credential",
                                        states: {
                                            credential: {
                                                on: {
                                                    GO_PROFILE: { target: "profile", actions: "trackAction" },
                                                    SELECT_USER: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                apiAuth: (context, event) => ({
                                                                    ...context.apiAuth,
                                                                    authType: "USERGROUP",
                                                                    userGroup: event.userGroup,
                                                                }),
                                                            }),
                                                            "trackAction",
                                                        ],
                                                    },
                                                    SET_USERNAME: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                apiAuth: (context, event) => ({
                                                                    ...context.apiAuth,
                                                                    signupForm: {
                                                                        ...context.apiAuth.signupForm,
                                                                        username: event.username.toLowerCase(),
                                                                    },
                                                                }),
                                                            }),
                                                        ],
                                                    },
                                                    SET_PASSWORD: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                apiAuth: (context, event) => ({
                                                                    ...context.apiAuth,
                                                                    signupForm: {
                                                                        ...context.apiAuth.signupForm,
                                                                        password: event.password,
                                                                    },
                                                                }),
                                                            }),
                                                        ],
                                                    },
                                                    SET_INVITATION_TOKEN: {
                                                        actions: [
                                                            SimulatorModel.assign({
                                                                apiAuth: (context, event) => ({
                                                                    ...context.apiAuth,
                                                                    signupForm: {
                                                                        ...context.apiAuth.signupForm,
                                                                        invitationCode: event.invitationCode,
                                                                    },
                                                                }),
                                                            }),
                                                        ],
                                                    },
                                                },
                                            },
                                            profile: {
                                                on: {
                                                    GO_CREDENTIAL: { target: "credential", actions: "trackAction" },
                                                    GO_CONFIRMATION: { target: "confirmation", actions: "trackAction" },
                                                },
                                                initial: "profileForm",
                                                states: {
                                                    profileForm: {
                                                        on: {
                                                            SET_PROFILE: {
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (ctx, event) => ({
                                                                            ...ctx.apiAuth,
                                                                            signUpProfileForm: event.values,
                                                                        }),
                                                                    }),
                                                                    "trackAction",
                                                                ],
                                                            },
                                                            FINISH_SIGNUP: {
                                                                target: "creatingUser",
                                                                actions: "trackAction",
                                                            },
                                                        },
                                                    },
                                                    creatingUser: {
                                                        type: "atomic",
                                                        invoke: {
                                                            id: "simulatorMachine-createUser",
                                                            src: "createUser",
                                                        },
                                                        on: {
                                                            SIGNED_UP: {
                                                                target: "#confirmation",
                                                                actions: "trackAction",
                                                            },
                                                            ERROR: {
                                                                target: "profileForm",
                                                                actions: "trackAction",
                                                            },
                                                            SIGNED_UP_WITH_INVITATION: {
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (context, event) => {
                                                                            const newSession = {
                                                                                authType: "USERGROUP",
                                                                                username: context.apiAuth.signupForm.username,
                                                                                authToken: event.authToken,
                                                                                userGroup: context.apiAuth.userGroup,
                                                                                refreshToken: event.refreshToken,
                                                                            } as AuthItem

                                                                            return {
                                                                                ...context.apiAuth,
                                                                                currentAuthSession: newSession,
                                                                                authSessionsList: [...context.apiAuth.authSessionsList, newSession],
                                                                            }
                                                                        },
                                                                    }),
                                                                    "prepareEndpoints",
                                                                    "checkAllRunability",
                                                                    "trackAction",
                                                                ],
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                            confirmation: {
                                                id: "confirmation",
                                                on: {
                                                    GO_PROFILE: { target: "profile", actions: "trackAction" },
                                                    RESEND_EMAIL: { target: "resendingEmail", actions: "trackAction" },
                                                },
                                                initial: "confirmForm",
                                                states: {
                                                    confirmForm: {
                                                        on: {
                                                            SET_CODE: {
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (context, event) => ({
                                                                            ...context.apiAuth,
                                                                            signupForm: {
                                                                                ...context.apiAuth.signupForm,
                                                                                emailCode: event.code,
                                                                            },
                                                                        }),
                                                                    }),
                                                                    "trackAction",
                                                                ],
                                                            },
                                                            CONFIRM_EMAIL: { target: "confirmingEmail", actions: "trackAction" },
                                                        },
                                                    },
                                                    confirmingEmail: {
                                                        type: "atomic",
                                                        invoke: {
                                                            id: "simulatorMachine-confirmingEmail",
                                                            src: "confirmingEmail",
                                                        },
                                                        on: {
                                                            CONFIRMED: {
                                                                target: "#login-session",
                                                                actions: [
                                                                    SimulatorModel.assign({
                                                                        apiAuth: (context, event) => {
                                                                            const newSession = {
                                                                                authType: "USERGROUP",
                                                                                username: context.apiAuth.signupForm.username,
                                                                                authToken: event.authToken,
                                                                                userGroup: context.apiAuth.userGroup,
                                                                                refreshToken: event.refreshToken,
                                                                            } as AuthItem

                                                                            return {
                                                                                ...context.apiAuth,
                                                                                currentAuthSession: newSession,
                                                                                authSessionsList: [...context.apiAuth.authSessionsList, newSession],
                                                                                signupForm: SimulatorModel.initialContext.apiAuth.signupForm,
                                                                                loginForm: SimulatorModel.initialContext.apiAuth.loginForm,
                                                                                signUpProfileForm:
                                                                                    SimulatorModel.initialContext.apiAuth.signUpProfileForm,
                                                                            }
                                                                        },
                                                                    }),
                                                                    "prepareEndpoints",
                                                                    "checkAllRunability",
                                                                    "trackAction",
                                                                ],
                                                            },
                                                            ERROR: {
                                                                target: "confirmForm",
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                            resendingEmail: {
                                                type: "atomic",
                                                invoke: {
                                                    id: "authMachine-resendEmail",
                                                    src: "resendEmailApiCall",
                                                },
                                                on: {
                                                    GO_CONFIRMATION: { target: "confirmation", actions: "trackAction" },
                                                    ERROR: { target: "confirmation", actions: "trackAction" },
                                                },
                                            },
                                        },
                                    },
                                    emailInvitation: {
                                        on: {
                                            TO_LOGIN_SESSION: { target: "loginSession", actions: "trackAction" },
                                            TO_SELF_SINGUP: { target: "selfSignup", actions: "trackAction" },
                                        },
                                        initial: "checkingState",
                                        states: {
                                            checkingState: {
                                                always: [
                                                    {
                                                        target: "splitScreen.leftSide.login",
                                                        cond: (context: SimulatorModelContext) => context.apiAuth.authSessionsList.length === 0,
                                                    },
                                                    {
                                                        target: "splitScreen.leftSide.sessions",
                                                        cond: (context: SimulatorModelContext) => context.apiAuth.authSessionsList.length > 0,
                                                    },
                                                ],
                                            },
                                            splitScreen: {
                                                type: "parallel",
                                                states: {
                                                    leftSide: {
                                                        initial: "sessions",
                                                        states: {
                                                            login: {
                                                                on: {
                                                                    GO_SESSIONS: { target: "sessions", actions: "trackAction" },
                                                                },
                                                                initial: "loginForm",
                                                                states: {
                                                                    loginForm: {
                                                                        on: {
                                                                            SELECT_USER: {
                                                                                actions: [
                                                                                    SimulatorModel.assign({
                                                                                        apiAuth: (context, event) => ({
                                                                                            ...context.apiAuth,
                                                                                            authType: "USERGROUP",
                                                                                            userGroup: event.userGroup,
                                                                                        }),
                                                                                    }),
                                                                                    "trackAction",
                                                                                ],
                                                                            },
                                                                            SET_USERNAME: {
                                                                                actions: [
                                                                                    SimulatorModel.assign({
                                                                                        apiAuth: (context, event) => ({
                                                                                            ...context.apiAuth,
                                                                                            loginForm: {
                                                                                                ...context.apiAuth.loginForm,
                                                                                                username: event.username.toLowerCase(),
                                                                                            },
                                                                                        }),
                                                                                    }),
                                                                                ],
                                                                            },
                                                                            SET_PASSWORD: {
                                                                                actions: [
                                                                                    SimulatorModel.assign({
                                                                                        apiAuth: (context, event) => ({
                                                                                            ...context.apiAuth,
                                                                                            loginForm: {
                                                                                                ...context.apiAuth.loginForm,
                                                                                                password: event.password,
                                                                                            },
                                                                                        }),
                                                                                    }),
                                                                                ],
                                                                            },
                                                                            LOGIN_CALL: {
                                                                                target: "logingIn",
                                                                                actions: "trackAction",
                                                                            },
                                                                        },
                                                                    },
                                                                    logingIn: {
                                                                        type: "atomic",
                                                                        invoke: {
                                                                            id: "simulatorMachine-logIn",
                                                                            src: "logIn",
                                                                        },
                                                                        on: {
                                                                            LOGED_IN: {
                                                                                target: "#email-login-sessions",
                                                                                actions: [
                                                                                    SimulatorModel.assign({
                                                                                        apiAuth: (context, event) => {
                                                                                            const newSession = {
                                                                                                authType: "USERGROUP",
                                                                                                username: context.apiAuth.loginForm.username,
                                                                                                userGroup: context.apiAuth.userGroup,
                                                                                                authToken: event.authToken,
                                                                                                refreshToken: event.refreshToken,
                                                                                            } as AuthItem

                                                                                            return {
                                                                                                ...context.apiAuth,
                                                                                                currentAuthSession: newSession,
                                                                                                authSessionsList: [
                                                                                                    ...context.apiAuth.authSessionsList,
                                                                                                    newSession,
                                                                                                ],
                                                                                                userGroup: "",
                                                                                            }
                                                                                        },
                                                                                    }),
                                                                                    "prepareEndpoints",
                                                                                    "closeEndpoint",
                                                                                    "checkAllRunability",
                                                                                    "trackAction",
                                                                                ],
                                                                            },
                                                                            ERROR: {
                                                                                target: "loginForm",
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                            sessions: {
                                                                id: "email-login-sessions",
                                                                on: {
                                                                    GO_LOGIN: { target: "login", actions: "trackAction" },
                                                                    SESSION_LOGOUT: {
                                                                        actions: [
                                                                            SimulatorModel.assign({
                                                                                apiAuth: (context) => ({
                                                                                    ...context.apiAuth,
                                                                                    currentAuthSession: undefined,
                                                                                }),
                                                                                gqlSimulator: (context) => ({
                                                                                    ...context.gqlSimulator,
                                                                                    invitationEndpoint: {},
                                                                                    visibleInvitationUserGroups: [],
                                                                                    acceptInvitationEndpoint: {},
                                                                                }),
                                                                            }),
                                                                            "prepareEndpoints",
                                                                            "closeEndpoint",
                                                                            "trackAction",
                                                                        ],
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                    // right side
                                                    signup: {
                                                        initial: "emailAddress",
                                                        states: {
                                                            emailAddress: {
                                                                initial: "default",
                                                                on: {
                                                                    SEND_INVITATION: {
                                                                        target: ".invitingUser",
                                                                        actions: ["closeEndpoint", "trackAction"],
                                                                    },
                                                                    SELECT_USER: {
                                                                        actions: [
                                                                            SimulatorModel.assign({
                                                                                gqlSimulator: (context, event) => ({
                                                                                    ...context.gqlSimulator,
                                                                                    invitationEndpoint: {
                                                                                        ...context.gqlSimulator.invitationEndpoint,
                                                                                        userGroup: event.userGroup,
                                                                                    },
                                                                                }),
                                                                            }),
                                                                            "trackAction",
                                                                        ],
                                                                    },
                                                                    SET_INVITATION_EMAIL: {
                                                                        actions: [
                                                                            SimulatorModel.assign({
                                                                                gqlSimulator: (context, event) => ({
                                                                                    ...context.gqlSimulator,
                                                                                    invitationEndpoint: {
                                                                                        ...context.gqlSimulator.invitationEndpoint,
                                                                                        email: event.email,
                                                                                    },
                                                                                }),
                                                                            }),
                                                                            "trackAction",
                                                                        ],
                                                                    },
                                                                },
                                                                states: {
                                                                    default: {},
                                                                    invitingUser: {
                                                                        type: "atomic",
                                                                        invoke: {
                                                                            id: "simulatorMachine-sendingInvitation",
                                                                            src: "sendingInvitation",
                                                                        },
                                                                        on: {
                                                                            INVITED: {
                                                                                target: "#signUp-profile",
                                                                                actions: ["trackAction"],
                                                                            },
                                                                            ERROR: {
                                                                                target: "default",
                                                                                actions: "trackAction",
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                            profile: {
                                                                id: "signUp-profile",
                                                                on: {
                                                                    GO_EMAIL_ADDRESS: { target: "emailAddress", actions: "trackAction" },
                                                                    SET_PROFILE: {
                                                                        actions: [
                                                                            SimulatorModel.assign({
                                                                                apiAuth: (ctx, event) => {
                                                                                    return {
                                                                                        ...ctx.apiAuth,
                                                                                        signUpProfileForm: event.values,
                                                                                    }
                                                                                },
                                                                            }),
                                                                            "trackAction",
                                                                        ],
                                                                    },
                                                                    FINISH_PROFILE: {
                                                                        target: "confirmation",
                                                                        actions: ["trackAction"],
                                                                    },
                                                                },
                                                            },
                                                            confirmation: {
                                                                on: {
                                                                    GO_PROFILE: { target: "profile", actions: "trackAction" },
                                                                },
                                                                initial: "confirmForm",
                                                                states: {
                                                                    confirmForm: {
                                                                        on: {
                                                                            SET_ACCEPT_INVITATION_CODE: {
                                                                                actions: [
                                                                                    SimulatorModel.assign({
                                                                                        gqlSimulator: (context, event) => ({
                                                                                            ...context.gqlSimulator,
                                                                                            invitationEndpoint: {
                                                                                                ...context.gqlSimulator.invitationEndpoint,
                                                                                                invitationCode: event.invitationCode,
                                                                                            },
                                                                                        }),
                                                                                    }),
                                                                                ],
                                                                            },
                                                                            SET_ACCEPT_INVITATION_PASSWORD: {
                                                                                actions: [
                                                                                    SimulatorModel.assign({
                                                                                        gqlSimulator: (context, event) => {
                                                                                            return {
                                                                                                ...context.gqlSimulator,
                                                                                                invitationEndpoint: {
                                                                                                    ...context.gqlSimulator.invitationEndpoint,
                                                                                                    password: event.password,
                                                                                                },
                                                                                            }
                                                                                        },
                                                                                    }),
                                                                                ],
                                                                            },
                                                                            ACCEPT_INVITATION: {
                                                                                target: "creatingUserByInvitation",
                                                                                actions: ["closeEndpoint", "trackAction"],
                                                                            },
                                                                        },
                                                                    },
                                                                    creatingUserByInvitation: {
                                                                        type: "atomic",
                                                                        invoke: {
                                                                            id: "simulatorMachine-createUser",
                                                                            src: "createUser",
                                                                        },
                                                                        on: {
                                                                            ERROR: {
                                                                                target: "confirmForm",
                                                                            },
                                                                            ACCEPTED_INVITATION: {
                                                                                target: "#login-session",
                                                                                actions: [
                                                                                    SimulatorModel.assign({
                                                                                        apiAuth: (context, event) => {
                                                                                            const newSession = {
                                                                                                authType: "USERGROUP",
                                                                                                username:
                                                                                                    context.gqlSimulator.invitationEndpoint?.email,
                                                                                                authToken: event.authToken,
                                                                                                userGroup:
                                                                                                    context.gqlSimulator.invitationEndpoint
                                                                                                        ?.userGroup,
                                                                                                refreshToken: event.refreshToken,
                                                                                            } as AuthItem

                                                                                            return {
                                                                                                ...context.apiAuth,
                                                                                                currentAuthSession: newSession,
                                                                                                authSessionsList: [
                                                                                                    ...context.apiAuth.authSessionsList,
                                                                                                    newSession,
                                                                                                ],
                                                                                                signupForm:
                                                                                                    SimulatorModel.initialContext.apiAuth.signupForm,
                                                                                                loginForm:
                                                                                                    SimulatorModel.initialContext.apiAuth.loginForm,
                                                                                                signUpProfileForm:
                                                                                                    SimulatorModel.initialContext.apiAuth
                                                                                                        .signUpProfileForm,
                                                                                            }
                                                                                        },
                                                                                        gqlSimulator: (context) => ({
                                                                                            ...context.gqlSimulator,
                                                                                            invitationEndpoint: {},
                                                                                        }),
                                                                                    }),
                                                                                    "prepareEndpoints",
                                                                                    "checkAllRunability",
                                                                                    "trackAction",
                                                                                ],
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    },
    {
        // shared actions
        actions: {
            trackAction: (context, event) => {
                // console.log("ACTION_EVENT", event.type)
                const session = localStorage.getItem("session")
                const sessionObj = session && JSON.parse(session)
                // axios
                //     .post(
                //         `${process.env.REACT_APP_QUORINI_API}/track-action`,
                //         {
                //             eventName: event.type,
                //             sessionId: context.sessionId,
                //         },
                //         {
                //             headers: { Authorization: sessionObj?.accessToken },
                //         }
                //     )
                //     .then((resp) => {
                //         // console.log("ACTION_EVENT_SAVED", event.type)
                //     })
                //     .catch((e) => {
                //         console.error(e)
                //     })
            },
            selectEndpoint: SimulatorModel.assign({
                gqlSimulator: (context, event) => {
                    if (
                        ![
                            "SELECT_ENDPOINT",
                            "OPEN_FILTER",
                            "OPEN_SELECTOR_FIELDS",
                            "OPEN_MUTATION_FORM",
                            "OPEN_CODE_SAMPLE",
                            "RUN",
                            "SHOW_RESPONSE",
                        ].includes(event.type)
                    )
                        return context.gqlSimulator

                    return {
                        ...context.gqlSimulator,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        selectedEndpoint: event.endpointName,
                    }
                },
            }),
            closeEndpoint: SimulatorModel.assign({
                gqlSimulator: (context) => ({
                    ...context.gqlSimulator,
                    selectedEndpoint: "",
                }),
            }),
            prepareEndpoints: SimulatorModel.assign({
                gqlSimulator: (context) => {
                    const visibleInvitationUserGroups: string[] = []
                    if (context.apiAuth.currentAuthSession?.userGroup) {
                        for (const u of context.projectConfig.users) {
                            if (u.invitableBy?.includes(context.apiAuth.currentAuthSession?.userGroup)) {
                                visibleInvitationUserGroups.push(u.groupName)
                            }
                        }
                    }

                    const visibleQueryEndpoints: VisibleQueryEndpoint[] = []
                    for (const ge of context.generatedEndpoints.queries.get) {
                        const entityName = ge.name.slice(3)
                        const entity = GetEntity(context.projectConfig, entityName)
                        const description = entity?.description

                        // console.log(JSON.stringify({ GE: ge, SESSION: context.apiAuth.currentAuthSession }))
                        if (
                            !context.apiAuth.currentAuthSession?.userGroup ||
                            ge.endpointVariants.findIndex((ev) => ev.accessItem.userGroupName == context.apiAuth.currentAuthSession?.userGroup) !== -1
                        ) {
                            visibleQueryEndpoints.push({
                                type: "GET",
                                name: ge.name,
                                entityName,
                                isInputOutputAll: true,
                                description,
                                isPublic: ge.endpointVariants.findIndex((ev) => ev.accessItem.type === "PUBLIC") !== -1,
                                // TODO: probably might need later to check AUTH access type
                            })
                        }
                        // FIXME: check runability directly with no action call

                        for (const ev of ge.endpointVariants) {
                            if (!ev.accessItem.rule?.name) continue
                            const evName = ge.name + "For" + ev.accessItem.rule.name

                            if (ev.accessItem.type !== "PUBLIC" && context.apiAuth.currentAuthSession?.userGroup !== ev.accessItem.userGroupName)
                                continue

                            visibleQueryEndpoints.push({
                                type: "GET",
                                name: evName,
                                entityName,
                                isInputOutputAll: ev.accessItem.isInputOutputAll,
                                outputFields: ev.outputFields,
                                outputSubfields: ev.outputSubfields,
                                description: ev.accessItem.rule.description,
                                isPublic: ev.accessItem.type === "PUBLIC",
                            })
                        }
                    }
                    for (const le of context.generatedEndpoints.queries.list) {
                        const entityName = getSingular(le.name.slice(4))
                        const entity = GetEntity(context.projectConfig, entityName)
                        const description = entity?.description

                        if (
                            !context.apiAuth.currentAuthSession?.userGroup ||
                            le.endpointVariants.findIndex((ev) => ev.accessItem.userGroupName == context.apiAuth.currentAuthSession?.userGroup) !== -1
                        ) {
                            visibleQueryEndpoints.push({
                                type: "LIST",
                                name: le.name,
                                entityName,
                                isInputOutputAll: true,
                                description,
                                isPublic: le.endpointVariants.findIndex((ev) => ev.accessItem.type === "PUBLIC") !== -1,
                            })
                        }

                        for (const ev of le.endpointVariants) {
                            if (!ev.accessItem.rule?.name) continue
                            const evName = le.name + "For" + ev.accessItem.rule.name

                            if (ev.accessItem.type !== "PUBLIC" && context.apiAuth.currentAuthSession?.userGroup !== ev.accessItem.userGroupName)
                                continue

                            visibleQueryEndpoints.push({
                                type: "LIST",
                                name: evName,
                                entityName,
                                isInputOutputAll: ev.accessItem.isInputOutputAll,
                                outputFields: ev.outputFields,
                                outputSubfields: ev.outputSubfields,
                                description: ev.accessItem.rule.description,
                                isPublic: ev.accessItem.type === "PUBLIC",
                            })
                        }
                    }

                    const visibleMutationEndpoints: VisibleMutationEndpoint[] = []

                    for (const ce of context.generatedEndpoints.mutations.create) {
                        const entityName = ce.name.slice(6)
                        const entity = GetEntity(context.projectConfig, entityName)
                        const description = entity?.description

                        if (
                            !context.apiAuth.currentAuthSession?.userGroup ||
                            ce.endpointVariants.findIndex((ev) => ev.accessItem.userGroupName == context.apiAuth.currentAuthSession?.userGroup) !== -1
                        ) {
                            visibleMutationEndpoints.push({
                                type: "CREATE",
                                name: ce.name,
                                entityName,
                                isInputOutputAll: true,
                                description,
                                isPublic: ce.endpointVariants.findIndex((ev) => ev.accessItem.type === "PUBLIC") !== -1,
                            })
                        }

                        for (const ev of ce.endpointVariants) {
                            if (!ev.accessItem.rule?.name) continue
                            const evName = ce.name + "For" + ev.accessItem.rule.name

                            if (ev.accessItem.type !== "PUBLIC" && context.apiAuth.currentAuthSession?.userGroup !== ev.accessItem.userGroupName)
                                continue

                            visibleMutationEndpoints.push({
                                type: "CREATE",
                                name: evName,
                                entityName,
                                isInputOutputAll: ev.accessItem.isInputOutputAll,
                                outputFields: ev.outputFields,
                                inputFields: ev.inputFields,
                                outputSubfields: ev.outputSubfields,
                                description: ev.accessItem.rule.description,
                                isPublic: ev.accessItem.type === "PUBLIC",
                            })
                        }
                    }

                    for (const ue of context.generatedEndpoints.mutations.update) {
                        const entityName = ue.name.slice(6)
                        const entity = GetEntity(context.projectConfig, entityName)
                        const description = entity?.description

                        if (
                            !context.apiAuth.currentAuthSession?.userGroup ||
                            ue.endpointVariants.findIndex((ev) => ev.accessItem.userGroupName == context.apiAuth.currentAuthSession?.userGroup) !== -1
                        ) {
                            visibleMutationEndpoints.push({
                                type: "UPDATE",
                                name: ue.name,
                                entityName,
                                isInputOutputAll: true,
                                description,
                            })
                        }

                        for (const ev of ue.endpointVariants) {
                            if (!ev.accessItem.rule?.name) continue
                            const evName = ue.name + "For" + ev.accessItem.rule.name

                            if (ev.accessItem.type !== "PUBLIC" && context.apiAuth.currentAuthSession?.userGroup !== ev.accessItem.userGroupName)
                                continue

                            visibleMutationEndpoints.push({
                                type: "UPDATE",
                                name: evName,
                                entityName,
                                isInputOutputAll: ev.accessItem.isInputOutputAll,
                                inputFields: ev.inputFields,
                                outputFields: ev.outputFields,
                                outputSubfields: ev.outputSubfields,
                                description: ev.accessItem.rule.description,
                                isPublic: ev.accessItem.type === "PUBLIC",
                            })
                        }
                    }

                    for (const de of context.generatedEndpoints.mutations.delete) {
                        const entityName = de.name.slice(6)
                        const entity = GetEntity(context.projectConfig, entityName)
                        const description = entity?.description

                        if (
                            !context.apiAuth.currentAuthSession?.userGroup ||
                            de.endpointVariants.findIndex((ev) => ev.accessItem.userGroupName == context.apiAuth.currentAuthSession?.userGroup) !== -1
                        ) {
                            visibleMutationEndpoints.push({
                                type: "DELETE",
                                name: de.name,
                                entityName,
                                isInputOutputAll: true,
                                description,
                            })
                        }
                    }

                    return {
                        ...context.gqlSimulator,
                        visibleInvitationUserGroups,
                        visibleQueryEndpoints,
                        visibleMutationEndpoints,
                        invitationEndpoint: {},
                        acceptInvitationEndpoint: {},
                    }
                },
            }),
            checkAllRunability: SimulatorModel.assign({
                gqlSimulator: (context) => {
                    const endpointsData = context.gqlSimulator.endpointsData

                    for (const endpoint of [
                        ...(context.gqlSimulator.visibleMutationEndpoints ?? []),
                        ...(context.gqlSimulator.visibleQueryEndpoints ?? []),
                    ]) {
                        const selectedEndpointData = endpointsData[endpoint.name] ?? {
                            status: "",
                            filters: [],
                            selectorFields: ["id"],
                            inputs: {},
                        }
                        const preventRunErrors = checkEndpointRunablity(context, endpoint.name, selectedEndpointData)

                        endpointsData[endpoint.name] = {
                            ...selectedEndpointData,
                            preventRunErrors,
                        }
                    }

                    return {
                        ...context.gqlSimulator,
                        endpointsData,
                    }
                },
            }),
            checkRunability: SimulatorModel.assign({
                gqlSimulator: (context, event) => {
                    const endpointName =
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        Object.keys(event).includes("endpointName") ? event["endpointName"]! : context.gqlSimulator.selectedEndpoint

                    const selectedEndpointData = context.gqlSimulator.endpointsData[endpointName] ?? {
                        status: "",
                        filters: [],
                        selectorFields: ["id"],
                        inputs: {},
                    }
                    if (!selectedEndpointData) return context.gqlSimulator
                    const preventRunErrors = checkEndpointRunablity(context, endpointName, selectedEndpointData)

                    return {
                        ...context.gqlSimulator,
                        endpointsData: {
                            ...context.gqlSimulator.endpointsData,
                            [context.gqlSimulator.selectedEndpoint]: {
                                ...selectedEndpointData,
                                preventRunErrors,
                            },
                        },
                    }
                },
            }),
            constructEndpointPayload: SimulatorModel.assign({
                gqlSimulator: (ctx) => {
                    const selectedEndpoint = ctx.gqlSimulator.selectedEndpoint
                    const selectedEndpointData = ctx.gqlSimulator.endpointsData[selectedEndpoint]
                    const selectedEndpointQueryData = ctx.gqlSimulator.visibleQueryEndpoints?.find((qe) => qe.name == selectedEndpoint)
                    const selectedEndpointMutationData = ctx.gqlSimulator.visibleMutationEndpoints?.find((me) => me.name == selectedEndpoint)
                    const visibleInvitationUserGroups = ctx.gqlSimulator.visibleInvitationUserGroups

                    let endpointType = ""
                    let endpointSubtype = ""
                    const endpointObjectId = selectedEndpointData.objectId
                    let endpointParamsPart = ""
                    if (selectedEndpointQueryData) {
                        endpointType = "query"
                        endpointSubtype = selectedEndpointQueryData.type.toLowerCase()
                        if (endpointSubtype == "get") endpointParamsPart = `(id: "${endpointObjectId}")`

                        const filters = selectedEndpointData.filters
                        if (filters.length > 0) {
                            if (endpointSubtype == "get") {
                                endpointParamsPart = `(id: "${endpointObjectId}", ${transformFilters(filters)})`
                            } else {
                                endpointParamsPart = `(${transformFilters(filters)})`
                            }
                        }
                    }

                    const inputs = selectedEndpointData.inputs
                    const selectorFields = selectedEndpointData.selectorFields
                    let selectorString = transformSelectors(selectorFields)
                    let variablesTypePart = ""
                    if (selectedEndpointMutationData) {
                        endpointType = "mutation"
                        endpointSubtype = selectedEndpointMutationData.type.toLowerCase()
                        if (endpointSubtype == "delete") {
                            endpointParamsPart = `(id: "${endpointObjectId}")`
                            selectorString = "id"
                        }

                        if (Object.keys(inputs).length > 0) {
                            endpointParamsPart = "(input: $input)"
                            if (visibleInvitationUserGroups?.includes(selectedEndpointMutationData.entityName)) {
                                endpointParamsPart = "(input: $input, withInvitation: true)"
                            }
                            variablesTypePart = "($input: " + selectedEndpoint + "Input!)"
                        }
                    }

                    const payload = {
                        query: `${endpointType} ${endpointSubtype}${variablesTypePart} { ${selectedEndpoint}${endpointParamsPart} { ${selectorString} } }`.replace(
                            /(\r\n|\n|\r)/gm,
                            " "
                        ),
                        ...(endpointSubtype === "delete"
                            ? {}
                            : { variables: { input: Object.keys(inputs).length > 0 ? { ...inputs, id: endpointObjectId } : {} } }),
                    }

                    return {
                        ...ctx.gqlSimulator,
                        endpointsData: {
                            ...ctx.gqlSimulator.endpointsData,
                            [ctx.gqlSimulator.selectedEndpoint]: {
                                ...selectedEndpointData,
                                payload,
                            },
                        },
                    }
                },
            }),
        },
        // API calls
        services: {
            // Quorini API call
            getProject: (ctx) => (send) => {
                const session = localStorage.getItem("session")
                const sessionObj = session && JSON.parse(session)

                axios
                    .get(`${process.env.REACT_APP_QUORINI_API}/get-project/${ctx.projectId}`, {
                        headers: {
                            Authorization: sessionObj?.accessToken,
                        },
                    })
                    .then((projectResp) => {
                        // console.log(projectResp)
                        axios
                            .get(`${process.env.REACT_APP_QUORINI_API}/${ctx.projectId}/docs`, {
                                headers: {
                                    Authorization: sessionObj?.accessToken,
                                },
                            })
                            .then((docsResp) => {
                                // console.log(docsResp)
                                send({
                                    type: "FETCHED",
                                    // Raw config and generated endpoints
                                    projectConfig: projectResp.data.latestActiveConfiguration,
                                    generatedEndpoints: projectResp.data.latestGeneratedEndpoints,
                                    // TS docs
                                    clientTypes: docsResp.data.ts?.clientTypes ?? "",
                                    queries: docsResp.data.ts?.queries ?? "",
                                    mutations: docsResp.data.ts?.mutations ?? "",
                                    //
                                })
                            })
                    })
                    .catch((e) => {
                        console.error(e)
                        send("ERROR")
                    })
            },
            // User's Project related API calls
            logIn: (ctx) => (send) => {
                const url = `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${ctx.projectId}/log-in${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`
                axios
                    .post(url, {
                        authOption: { username: ctx.apiAuth.loginForm.username, password: ctx.apiAuth.loginForm.password },
                    })
                    .then((resp) => {
                        send({ type: "LOGED_IN", authToken: resp.data.accessToken, refreshToken: resp.data.refreshToken })
                        ctx.notificationsApi?.success({
                            message: "Authenticated",
                            description: "New user session was successfully added!",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
                    .catch((e) => {
                        console.error(e)
                        send("ERROR")
                        ctx.notificationsApi?.error({
                            message: "Error",
                            description: "Failed to create new session, try again...",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
            },
            refreshAuthToken: (context) => (send) => {
                const updatedSessionList = context.apiAuth.authSessionsList as AuthItem[]
                let updatedSession = context.apiAuth.currentAuthSession as AuthItem
                const updatedSessionUsername = updatedSession.username
                const sessionIndex = updatedSessionList.findIndex((s) => s.username === updatedSessionUsername)
                const storageSessions = JSON.parse(localStorage.getItem("simulator-sessions") as string) as LocalStorageSessions

                axios
                    .post(
                        `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${context.projectId}/refresh-token/${context.gqlSimulator.env !== "production" ? `?env=${context.gqlSimulator.env}` : ""}`,
                        {
                            refreshToken: updatedSession.refreshToken,
                        }
                    )
                    .then((resp) => {
                        if (resp.status === 200 && resp.data.accessToken) {
                            updatedSession = {
                                ...updatedSession,
                                authToken: resp.data.accessToken,
                            }
                            updatedSessionList[sessionIndex] = updatedSession
                        }
                        storageSessions[context.projectId][context.gqlSimulator.env] = updatedSessionList
                        localStorage.setItem("simulator-sessions", JSON.stringify(storageSessions))
                        send({
                            type: "AUTH_TOKEN_REFRESHED",
                            authSessionsList: updatedSessionList,
                            currentAuthSession: updatedSession,
                        })
                    })
                    .catch((error) => {
                        console.error("Failed to refresh token", error)

                        // remove session from list of sessions
                        updatedSessionList.splice(sessionIndex, 1)
                        updatedSession = {} as AuthItem

                        context.notificationsApi?.error({
                            message: "Your Session has expired",
                            description: `Failed to refresh token for "${updatedSessionUsername}", please re-login again...`,
                            placement: "bottomLeft",
                            duration: 5,
                        })

                        send({
                            type: "AUTH_TOKEN_REFRESHED",
                            authSessionsList: updatedSessionList,
                            currentAuthSession: updatedSession,
                        })
                    })
            },
            // Called when user A directly signs up
            // And also called by user B after user A sent invitation to user B
            createUser: (ctx, e) => (send) => {
                // console.log("createUser action type", e.type)

                const params =
                    e.type === "ACCEPT_INVITATION"
                        ? {
                              username: ctx.gqlSimulator.invitationEndpoint?.email,
                              password: ctx.gqlSimulator.invitationEndpoint?.password,
                              invitationCode: ctx.gqlSimulator.invitationEndpoint?.invitationCode,
                              userGroup: ctx.gqlSimulator.invitationEndpoint?.userGroup,
                          }
                        : {
                              username: ctx.apiAuth.signupForm.username,
                              password: ctx.apiAuth.signupForm.password,
                              invitationCode: ctx.apiAuth.signupForm.invitationCode,
                              userGroup: ctx.apiAuth.userGroup,
                          }

                const url = `${process.env.REACT_APP_CUSTOMER_API}/${ctx.projectId}/gql${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`
                axios
                    .post(url, {
                        authOption: {
                            username: params.username,
                            password: params.password,
                            invitationCode: params.invitationCode,
                        },
                        query: `mutation create($input: create${params.userGroup}Input!) { create${params.userGroup}(input: $input) { id }}`,
                        variables: {
                            input: {
                                ...ctx.apiAuth.signUpProfileForm,
                            },
                        },
                    })
                    .then((resp) => {
                        if (resp.data.errors !== null && resp.data.errors.length !== 0) {
                            console.log(resp)
                            throw new Error("Failed user creation")
                        }

                        if (params.invitationCode) {
                            const url = `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${ctx.projectId}/log-in${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`
                            axios
                                .post(url, {
                                    authOption: { username: params.username, password: params.password },
                                })
                                .then((resp) => {
                                    send({
                                        type: e.type === "ACCEPT_INVITATION" ? "ACCEPTED_INVITATION" : "SIGNED_UP_WITH_INVITATION",
                                        authToken: resp.data.accessToken,
                                        refreshToken: resp.data.refreshToken,
                                    })
                                    ctx.notificationsApi?.success({
                                        message: "Signed up and authenticated",
                                        description: "New user was successfully created and new session added!",
                                        placement: "bottomLeft",
                                        duration: 5,
                                    })
                                })
                        } else {
                            send({ type: "SIGNED_UP" })
                            ctx.notificationsApi?.success({
                                message: "Signed up",
                                description: "New user was successfully created! Now verify email",
                                placement: "bottomLeft",
                                duration: 5,
                            })
                        }
                    })
                    .catch((e) => {
                        console.error(e)
                        send("ERROR")
                        ctx.notificationsApi?.error({
                            message: "Error",
                            description: "Failed to create new user, try again...",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
            },
            confirmingEmail: (ctx) => (send) => {
                axios
                    .get(
                        `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${ctx.projectId}/verify-email/?code=${ctx.apiAuth.signupForm.emailCode}&username=${ctx.apiAuth.signupForm.username.replace("+", "%2B")}${ctx.gqlSimulator.env !== "production" ? `&env=${ctx.gqlSimulator.env}` : ""}`,
                        {}
                    )
                    .then(() => {
                        axios
                            .post(
                                `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${ctx.projectId}/log-in${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`,
                                {
                                    authOption: { username: ctx.apiAuth.signupForm.username, password: ctx.apiAuth.signupForm.password },
                                }
                            )
                            .then((resp) => {
                                // console.log(resp)
                                send({ type: "CONFIRMED", authToken: resp.data.accessToken, refreshToken: resp.data.refreshToken })
                                ctx.notificationsApi?.success({
                                    message: "Verified and authenticated",
                                    description: "Email address successfully verified and new session added!",
                                    placement: "bottomLeft",
                                    duration: 5,
                                })
                            })
                    })
                    .catch((e) => {
                        console.error(e)
                        send("ERROR")
                        ctx.notificationsApi?.error({
                            message: "Error",
                            description: "Failed to verify email address, try again...",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
            },
            runEndpoint: (ctx) => async (send) => {
                const selectedEndpoint = ctx.gqlSimulator.selectedEndpoint

                let breakMutation = false
                const selectedEndpointData = ctx.gqlSimulator.endpointsData[selectedEndpoint]
                // console.log(selectedEndpointData)
                if (selectedEndpointData && selectedEndpointData.payload.variables?.input) {
                    const inputs = Object.keys(selectedEndpointData.payload.variables.input)

                    // console.log(inputs, selectedEndpointData.payload.variables.input)

                    for (const k of inputs) {
                        if (k.endsWith("_FILE")) {
                            if (Array.isArray(selectedEndpointData.payload.variables.input[k])) {
                                const fileIds: string[] = []
                                for (let i = 0; i < selectedEndpointData.payload.variables.input[k].length; i++) {
                                    const fileObj = selectedEndpointData.payload.variables.input[k][i]
                                    if (!fileObj) {
                                        const endTime = new Date()
                                        send({
                                            type: "COMPLETED",
                                            raw: JSON.stringify({ data: null, errors: [new Error("File is not selected")] }),
                                            endpointName: selectedEndpoint,
                                            endTime,
                                        })
                                        ctx.notificationsApi?.error({
                                            message: "Error",
                                            description: "Failed to read file, try again...",
                                            placement: "bottomLeft",
                                            duration: 5,
                                        })
                                        breakMutation = true
                                        break
                                    }

                                    let fileId
                                    await axios
                                        .get(
                                            `${process.env.REACT_APP_CUSTOMER_API}/${ctx.projectId}/file-upload-url${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`,
                                            {
                                                headers: {
                                                    Authorization: ctx.apiAuth.currentAuthSession?.authToken,
                                                },
                                            }
                                        )
                                        .then(async (resp) => {
                                            const fileUploadUrl = resp.data.uploadUrl
                                            fileId = resp.data.fileId
                                            fileIds.push(fileId)

                                            await axios.put(fileUploadUrl, fileObj, {
                                                headers: {
                                                    "Content-Type": fileObj.type,
                                                },
                                                onUploadProgress: (e) => {
                                                    //  Show progress
                                                    const percentCompleted = Math.round((e.loaded * 100) / fileObj.size)

                                                    console.log(percentCompleted) // TODO: show progress bar next to file input
                                                },
                                            })
                                        })
                                        .catch((err) => {
                                            console.error(err)
                                            const endTime = new Date()
                                            breakMutation = true
                                            send({
                                                type: "COMPLETED",
                                                raw: JSON.stringify({ data: null, errors: [err] }),
                                                endpointName: selectedEndpoint,
                                                endTime,
                                            })
                                            ctx.notificationsApi?.error({
                                                message: "Error",
                                                description: "Failed to upload file, try again...",
                                                placement: "bottomLeft",
                                                duration: 5,
                                            })
                                        })
                                }
                                selectedEndpointData.payload.variables.input[k.split("_FILE")[0]] = fileIds
                                delete selectedEndpointData.payload.variables.input[k]
                            } else {
                                const fileObj = selectedEndpointData.payload.variables.input[k]
                                if (fileObj) {
                                    let fileId
                                    await axios
                                        .get(
                                            `${process.env.REACT_APP_CUSTOMER_API}/${ctx.projectId}/file-upload-url${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`,
                                            {
                                                headers: {
                                                    Authorization: ctx.apiAuth.currentAuthSession?.authToken,
                                                },
                                            }
                                        )
                                        .then(async (resp) => {
                                            const fileUploadUrl = resp.data.uploadUrl
                                            fileId = resp.data.fileId

                                            await axios.put(fileUploadUrl, fileObj, {
                                                headers: {
                                                    "Content-Type": fileObj.type,
                                                },
                                                onUploadProgress: (e) => {
                                                    //  Show progress
                                                    const percentCompleted = Math.round((e.loaded * 100) / fileObj.size)

                                                    // console.log(percentCompleted) // TODO: show progress bar next to file input
                                                },
                                            })
                                        })
                                        .catch((err) => {
                                            console.error(err)
                                            const endTime = new Date()
                                            breakMutation = true
                                            send({
                                                type: "COMPLETED",
                                                raw: JSON.stringify({ data: null, errors: [err] }),
                                                endpointName: selectedEndpoint,
                                                endTime,
                                            })
                                            ctx.notificationsApi?.error({
                                                message: "Error",
                                                description: "Failed to upload file, try again...",
                                                placement: "bottomLeft",
                                                duration: 5,
                                            })
                                        })

                                    selectedEndpointData.payload.variables.input[k.split("_FILE")[0]] = fileId
                                }
                                delete selectedEndpointData.payload.variables.input[k]
                            }
                        }
                    }
                }

                if (breakMutation) return

                // console.log(ctx.gqlSimulator.endpointsData[selectedEndpoint].payload)

                axios
                    .post(
                        `${process.env.REACT_APP_CUSTOMER_API}/${ctx.projectId}/gql${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`,
                        ctx.gqlSimulator.endpointsData[selectedEndpoint].payload,
                        {
                            headers: { Authorization: ctx.apiAuth.currentAuthSession?.authToken },
                        }
                    )
                    .then((resp) => {
                        // console.log(resp)
                        const endTime = new Date()
                        send({ type: "COMPLETED", raw: resp.data, endpointName: selectedEndpoint, endTime })
                    })
                    .catch((err) => {
                        const endTime = new Date()
                        console.error(err)
                        send({
                            type: "COMPLETED",
                            raw: JSON.stringify({ data: null, errors: [err] }),
                            endpointName: selectedEndpoint,
                            endTime,
                        })
                    })
            },
            sendingInvitation: (ctx) => (send) => {
                axios
                    .post(
                        `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${ctx.projectId}/send-invitation${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`,
                        {
                            email: ctx.gqlSimulator.invitationEndpoint?.email,
                            userGroup: ctx.gqlSimulator.invitationEndpoint?.userGroup,
                        },
                        {
                            headers: { Authorization: ctx.apiAuth.currentAuthSession?.authToken },
                        }
                    )
                    .then((resp) => {
                        console.log(resp)
                        send({ type: "INVITED" })
                        ctx.notificationsApi?.success({
                            message: "Invited",
                            description: "User was successfully invited over email! Tell them to check email inbox.",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
                    .catch((e) => {
                        console.error(e)
                        send("ERROR")
                        ctx.notificationsApi?.error({
                            message: "Error",
                            description: "Failed to sent invitation, try again...",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
            },
            resendEmailApiCall: (ctx) => async (send) => {
                await axios
                    .get(
                        `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${ctx.projectId}/resend-verification-email/?username=${ctx.apiAuth.signupForm.username}${ctx.gqlSimulator.env !== "production" ? `&env=${ctx.gqlSimulator.env}` : ""}`
                    )
                    .then(() => {
                        send("GO_CONFIRMATION")
                    })
                    .catch((e) => {
                        console.error(e)
                        send("ERROR")
                        ctx.notificationsApi?.error({
                            message: "Error",
                            description: "Failed to resend email, please try again...",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
            },
            // Called by user B after user A sent createUserB mutation with "withInvitation" option
            acceptingInvitation: (ctx) => (send) => {
                axios
                    .post(
                        `${process.env.REACT_APP_CUSTOMER_AUTH_API}/${ctx.projectId}/accept-invitation${ctx.gqlSimulator.env !== "production" ? `?env=${ctx.gqlSimulator.env}` : ""}`,
                        {
                            authOption: {
                                email: ctx.gqlSimulator.acceptInvitationEndpoint?.email,
                                newPassword: ctx.gqlSimulator.acceptInvitationEndpoint?.password,
                                invitationCode: ctx.gqlSimulator.acceptInvitationEndpoint?.invitationCode,
                            },
                        }
                    )
                    .then((resp) => {
                        console.log(resp)
                        send({ type: "ACCEPTED_INVITATION", authToken: resp.data.accessToken, refreshToken: resp.data.refreshToken })
                        ctx.notificationsApi?.success({
                            message: "Accepted invitation",
                            description: "User successfully finished sign-up! Now login using above form.",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
                    .catch((e) => {
                        console.error(e)
                        send("ERROR")
                        ctx.notificationsApi?.error({
                            message: "Error",
                            description: "Failed to sent invitation, try again...",
                            placement: "bottomLeft",
                            duration: 5,
                        })
                    })
            },
        },
    }
)

function transformSelectors(selectors: string[]) {
    let result = ""
    const processedFields = new Set() // To track already processed root fields

    for (let i = 0; i < selectors.length; i++) {
        const selector = selectors[i]
        const parts = selector.split(".")

        // Skip if the root field has already been processed
        if (processedFields.has(parts[0])) {
            continue
        }

        if (result !== "") {
            result += " "
        }

        if (parts.length === 1) {
            // Simple field, just add it to the result
            result += parts[0]
        } else {
            // Complex field, need recursion
            const subSelectors = selectors.filter((s) => s.startsWith(parts[0] + ".")).map((s) => s.split(".").slice(1).join("."))

            result += parts[0] + " { " + transformSelectors(subSelectors) + " }"
        }

        // Mark the root field as processed
        processedFields.add(parts[0])
    }

    return result
}

function transformFilters(filters: EndpointFilter[]) {
    let filterString = ""
    for (const f of filters) {
        if (filterString != "") filterString += ", "
        switch (f.operator) {
            case "CONTAINS":
                if (typeof f.value == "object") {
                    filterString += `${f.field}: { contains: ${JSON.stringify(f.value)} }`
                } else if (typeof f.value == "string") {
                    filterString += `${f.field}: { contains: "${f.value} }"`
                } else {
                    filterString += `${f.field}: { contains: ${f.value} }`
                }
                break
            case "EQUAL":
                if (typeof f.value == "object") {
                    filterString += `${f.field}: { eq: ${JSON.stringify(f.value)} }`
                } else if (typeof f.value == "string") {
                    filterString += `${f.field}: { eq: "${f.value}" }`
                } else {
                    filterString += `${f.field}: { eq: ${f.value} }`
                }
                break
            case "IN":
                filterString += `${f.field}: { in: ${JSON.stringify(f.value)} }`
                break
            default:
                console.error("Unknown filter operator for the query, contact tech support")
        }
    }

    return `filter: { ${filterString} }`
}

export type SimulatorMachineState = StateFrom<typeof simulatorMachine>
export type SimulatorMachineSend = InterpreterFrom<typeof simulatorMachine>["send"]
